/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  Box,
  ExtendedPaginationBox,
  FlexContainer,
  Input,
  Sidebar,
  theme,
} from "@nordcloud/gnui";
import { SidebarItemList } from "./SidebarItemList";
import { Item, SidebarSelectorProps, UISidebarProps } from "./types";

type Props = SidebarSelectorProps & UISidebarProps;

export function SidebarSelector({
  limit,
  options = [],
  hasIcon,
  name,
  hasSubtitle,
  isOpen,
  onClick,
  onSubmit,
}: Props) {
  const [filteredOptions, setFilteredOptions] = React.useState<Item[]>([]);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setFilteredOptions(options);

    const inputRefCurrent = inputRef?.current;
    if (isOpen && inputRefCurrent) {
      inputRefCurrent.focus();
    }
  }, [options, isOpen]);

  const filterBySearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.toLowerCase());
    setPage(0);

    const searchValue = event.target.value.toLowerCase();
    setFilteredOptions(
      options.filter(
        ({ label, value }) =>
          label.toLowerCase().includes(searchValue) ||
          value.toLowerCase().includes(searchValue)
      )
    );
  };

  const sidebarItems =
    filteredOptions?.length > 10
      ? filteredOptions.slice(page * limit, (page + 1) * limit)
      : filteredOptions ?? [];

  return (
    <Sidebar
      title={`Select ${name}`}
      isOpen={isOpen}
      footer={
        <ExtendedPaginationBox
          sidebar
          small
          count={search.length === 0 ? options.length : filteredOptions.length}
          from={page * limit}
          setSize={() => undefined}
          size={limit}
          setPage={(newPage) => {
            setPage(Math.ceil(newPage / limit));
          }}
        />
      }
      onClick={onClick}
    >
      <Box boxStyle="lightGrey" mb={theme.spacing.spacing04}>
        <FlexContainer>
          <div css={{ flexGrow: 1 }}>
            <Input
              ref={inputRef}
              small
              type="text"
              placeholder="Search"
              onChange={filterBySearch}
            />
          </div>
        </FlexContainer>
      </Box>
      <SidebarItemList
        items={sidebarItems}
        hasSubtitle={hasSubtitle}
        hasIcon={hasIcon}
        onSubmit={onSubmit}
      />
    </Sidebar>
  );
}
